import { z } from 'zod';

const settingsColorSchema = z
  .object({
    primary: z.string().optional(),
    ctaBackground: z.string().optional(),
    ctaText: z.string().optional(),
  })
  .loose();

const customerPreviewSettingsSchema = z
  .object({
    showTotalPrice: z.boolean().optional(),
    showItemPrice: z.boolean().optional(),
  })
  .loose();

export const layoutTemplateSettingsSchema = z
  .object({
    color: settingsColorSchema.optional(),
    customerPreview: customerPreviewSettingsSchema.optional(),
    // Preview logo: the customer's agency logo when set (`agency`, the
    // default when absent) or always the layout's own (`layout`).
    logoSource: z.enum(['agency', 'layout']).optional(),
  })
  .loose();

export type LayoutTemplateSettings = z.infer<
  typeof layoutTemplateSettingsSchema
>;

export const layoutTemplateOrganizationSchema = z.object({
  id: z.uuid(),
  title: z.string().nullable(),
});

export const layoutTemplateItemSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  default: z.boolean(),
  uploadthingKey: z.string().nullable(),
  settings: layoutTemplateSettingsSchema,
  organizationId: z.uuid(),
  organization: layoutTemplateOrganizationSchema.nullable(),
});

export type LayoutTemplateItem = z.infer<typeof layoutTemplateItemSchema>;

export const createLayoutTemplateInput = z.object({
  title: z.string().min(1),
  default: z.boolean().optional(),
  uploadthingKey: z.string().nullable().optional(),
  settings: layoutTemplateSettingsSchema.optional(),
});

export type CreateLayoutTemplateInput = z.infer<
  typeof createLayoutTemplateInput
>;

export const updateLayoutTemplateInput = z.object({
  id: z.uuid(),
  title: z.string().min(1).optional(),
  default: z.boolean().optional(),
  uploadthingKey: z.string().nullable().optional(),
  settings: layoutTemplateSettingsSchema.optional(),
});

export type UpdateLayoutTemplateInput = z.infer<
  typeof updateLayoutTemplateInput
>;

export const findLayoutTemplatesInput = z.object({
  offset: z.number().int().min(0).optional(),
  limit: z.number().int().min(1).max(200).optional(),
  title: z.string().optional(),
  sort: z.enum(['title', 'createdOn', 'default']).optional(),
  order: z.enum(['asc', 'desc']).optional(),
});

export type FindLayoutTemplatesInput = z.infer<typeof findLayoutTemplatesInput>;

export const findLayoutTemplatesResultSchema = z.object({
  data: z.array(layoutTemplateItemSchema),
  count: z.number().int().nonnegative(),
  countAll: z.number().int().nonnegative(),
});

export type FindLayoutTemplatesResult = z.infer<
  typeof findLayoutTemplatesResultSchema
>;

export const deleteLayoutTemplateInput = z.object({ id: z.uuid() });

export type DeleteLayoutTemplateInput = z.infer<
  typeof deleteLayoutTemplateInput
>;

export const deleteLayoutTemplateResultSchema = z.object({ id: z.uuid() });

export type DeleteLayoutTemplateResult = z.infer<
  typeof deleteLayoutTemplateResultSchema
>;
